import {
  AmcDisplayName,
  AMCEnum,
  DateFormat,
  DescribedRennovation,
  DUPLEX,
  formatDate,
  insertIf,
  isEmptyString,
  isNotBlank,
  LoanSubType,
  MIXED_USE,
  MULTIFAMILY_5_PLUS,
  NEW_CONSTRUCTION,
  PURCHASE,
  QUADRUPLEX,
  REFINANCE,
  SINGLE_FAMILY,
  TRIPLEX,
} from '@roc/feature-utils';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { PreflightStep, PropertySource, QuoteStep, rentalPortfolioQuoteSteps, rentalPortfolioQuoteStepsWithoutPricer, singlePropertyRentalQuoteSteps, singlePropertyRentalQuoteStepsWithoutPricer, SquareFootageExpansion } from './constants';
import { SoftCostsOnly } from '../steps/propertyInformation/components/propertyInformationFields';

export function getUnitsFromPropertyType(propertyType: string): number | '' {
  switch (propertyType) {
    case SINGLE_FAMILY:
    case 'Townhome':
    case 'Planned Unit Development':
    case 'Condominium':
      return 1;
    case DUPLEX:
      return 2;
    case TRIPLEX:
      return 3;
    case QUADRUPLEX:
      return 4;
    case MULTIFAMILY_5_PLUS:
      return 5;
    default:
      return '';
  }
}

export function getValueOrDefaultDash(value): string {
  return value ? value.toString() : '-';
}

export function getBooleanDisplayValue(value): string {
  return value ? 'Yes' : 'No';
}

export function getYesNoDisplayValue(value): string {
  return value === 'Y' ? 'Yes' : 'No';
}

export const sum = values =>
  values.reduce((total, value) => total + (Number(value) || 0), 0);

export const parseDate = date => {
  return date ? formatDate(date, DateFormat.MMDDYYYY) : '-';
};

export const LTC_CATCHUP_PERCENTAGE = 15;

export const calculateLtcCatchupAmount = purchasePrice => {
  return LTC_CATCHUP_PERCENTAGE / 100 * purchasePrice;
};
export const mapTermSheetData = fields => {
  const formatCurrencyValue = value => {
    if (value === null || value === undefined) {
      return '-';
    }
    return formatCurrency(value, 2);
  };

  const formatPercentageValue = value => {
    if (value === null || value === undefined) {
      return '-';
    }
    return formatPercentage(value, 2);
  };
};

export const isRestrictedCounty = (countyValue: string, notAllowedCounties: string[], state: string, notAllowedStates: string[]): boolean => {
  if (!countyValue || !state) {
    return false;
  }

  const isRestrictedCount = notAllowedCounties.some(
    restricted =>
      countyValue === restricted ||
      countyValue?.startsWith(restricted + ' ') ||
      countyValue?.endsWith(' ' + restricted) ||
      countyValue?.includes(restricted)
  );

  return isRestrictedCount && notAllowedStates.includes(state);
};

export const mapPropertyFields = (values, loanSubType): any => {
  const ltcCatchupDrawAmount = calculateLtcCatchupAmount(
    values.purchasePrice ?? 0
  );
  const isPurchase = values.loanPurpose === PURCHASE;
  const isRefinance = values.loanPurpose === REFINANCE;
  const isWholesale = PropertySource.WHOLESALER == values.propertySourcing;
  const exitStrategyRefinance = values.exitStrategy === REFINANCE;
  const describeRenovation = loanSubType === LoanSubType.GROUND_UP
    ? (values.describeRenovation || NEW_CONSTRUCTION)
    : values.describeRenovation;
  const isAddingSquareFootage = DescribedRennovation.NO != describeRenovation;
  const data = {
    units: values.units,
    propertyId: values.propertyId,
    address: values.address,
    streetNumber: values.streetNumber,
    streetName: values.streetName,
    aptNumber: values.aptNumber,
    city: values.city,
    state: values.state,
    county: values.county,
    zipCode: values.zipCode,
    latitude: values.latitude,
    longitude: values.longitude,
    propertyType: values.propertyType,
    loanPurpose: values.loanPurpose,
    payoffAmount: values.payoffAmount,
    subdividedIntoLots: values.subdividedIntoLots,
    numberOfLots: values.numberOfLots,
    loanEverBeenInDefault: values.loanEverBeenInDefault,
    permitsInPlace: isAddingSquareFootage ? values.permitsInPlace : undefined,
    ltcCatchupDraw: values.ltcCatchupDraw ?? false,
    ltcCatchupDrawAmount: values.ltcCatchupDraw ? ltcCatchupDrawAmount : 0,
    hasBeenRecentlyRenovated: values.hasBeenRecentlyRenovated,
    soonToBeListedForSale: values.soonToBeListedForSale,
    currentlyOccupied: values.currentlyOccupied,
    annualTaxes: values.annualTaxes,
    monthlyMarketRent: values.monthlyMarketRent,
    annualInsurance: values.annualInsurance,
    holdingCosts: values.holdingCosts,
    currentMarketValue: values.currentMarketValue,
    renovationBudget: values.renovationBudget,
    marketValueAfterCompletion: values.marketValueAfterCompletion,
    squareFootageExpansion: values.squareFootageExpansion,
    isAppraisalAvailable: values.isAppraisalAvailable,
    purchasePrice: values.purchasePrice,
    wholesalerPropertyContractAmount: isWholesale
      ? values.wholesalerPropertyContractAmount
      : undefined,
    propertySourcingExplanation:
      PropertySource.OTHER == values.propertySourcing
        ? values.propertySourcingExplanation
        : undefined,

    ...(isPurchase
      ? {
        propertySourcing: values.propertySourcing,
        sellerCredit: values.sellerCredit,
        sellerCreditAmount: values.sellerCredit
          ? values.sellerCreditAmount
          : undefined,
        borrowerKnowOwnerOfRecord: values.borrowerKnowOwnerOfRecord,
        armsLengthComment: values.armsLengthComment,
        constructionRenoSpendToDate: values.constructionRenoSpendToDate,
      }
      : {}),
    ...(isRefinance
      ? {
        purchaseDate: formatDate(values.purchaseDate, DateFormat.MMDDYYYY),
        outstandingLoanOnTheProperty: values.outstandingLoanOnTheProperty,
        loanEverBeenInDefault: values.outstandingLoanOnTheProperty
          ? values.loanEverBeenInDefault
          : undefined,
        constructionRenoSpendToDate: values.constructionRenoSpendToDate,
      }
      : {}),
    condoConversion: describeRenovation ? describeRenovation == DescribedRennovation.CONDO_CONVERSION : values.condoConversion,
    fireDamage: describeRenovation ? describeRenovation == DescribedRennovation.FIRE_DAMAGE : values.fireDamage,
    squareFootageAdded: isAddingSquareFootage,
    hasNewAddress: values.hasNewAddress,
    exitStrategy: values.exitStrategy,
    newAddress: values.newAddress,
    ...(exitStrategyRefinance ? {
      monthlyMarketRentProvidedAtOrigination: values.monthlyMarketRentProvidedAtOrigination,
      annualHOAProvidedAtOrigination: values.annualHOAProvidedAtOrigination,
      annualTaxesProvidedAtOrigination: values.annualTaxesProvidedAtOrigination,
      annualInsuranceProvidedAtOrigination: values.annualInsuranceProvidedAtOrigination,
    } : {}),
    releaseAmount: values.releaseAmount, // Term Sheet
    propertyLoanAllocatedAmount: values.propertyLoanAllocatedAmount, // Term Sheet
    describeRenovation: describeRenovation,
    annualHOAProvidedAtOrigination: values.annualHOAProvidedAtOrigination,
    cashOut: values.cashOut,
    seekingCashOut: values.cashOut,
    isPropertyLeased: values.isPropertyLeased,
    section8: values.section8,
    overrideUnderwrittenAsIsValue: values.overrideUnderwrittenAsIsValue,
    propertyValuation: values.propertyValuation,
    softCostsOnly: values.softCostsOnly,
    medianZipCodeValue: values.medianZipCodeValue,
  };

  return Object.keys(data).reduce((obj, key) => {
    const hasValue =
      data[key] !== null && data[key] !== '' && data[key] !== undefined;
    if (hasValue) {
      obj[key] = data[key];
    }
    return obj;
  }, {});
};

export const buildAddress = (property) => {
  const address = property.address || '';
  const city = property.city || '';
  const state = property.state || '';
  const zipCode = property.zipCode || '';

  const fullAddress = `${address}, ${city}, ${state} ${zipCode}`.trim();

  return fullAddress;
}
export const formatPercentageValue = value => {
  if (value === null || value === undefined) {
    return '-';
  }
  return formatPercentage(value, 2);
};

export const getQuoteSteps = (loanSubtype: LoanSubType, allowLoanPricing: boolean) => {
  switch (loanSubtype) {
    case LoanSubType.SINGLE_PROPERTY: {
      if (!allowLoanPricing) {
        return singlePropertyRentalQuoteStepsWithoutPricer;
      }
      return singlePropertyRentalQuoteSteps;
    }
    case LoanSubType.RENTAL_PORTFOLIO: {
      if (!allowLoanPricing) {
        return rentalPortfolioQuoteStepsWithoutPricer;
      }
      return rentalPortfolioQuoteSteps;
    }
    default: {
      return [
        QuoteStep.ENTITY_INFORMATION,
        QuoteStep.BORROWER_INFORMATION,
        QuoteStep.PROPERTY_INFORMATION,
        QuoteStep.LEVERAGE,
        QuoteStep.LOAN_ECONOMICS,
        QuoteStep.SUMMARY,
        QuoteStep.PREFLIGHT,
        QuoteStep.SUCCESS,
      ];
    }
  }
}

export const getPreflightSteps = allowLoanPricing => {
  return [
    // PreflightStep.ENTITY,
    PreflightStep.BORROWER,
    PreflightStep.BROKER,
    PreflightStep.PROPERTY,
    ...insertIf(allowLoanPricing, [PreflightStep.CLOSING]),
    PreflightStep.SUBMISSION,
  ];
};

export function getAmcFromId(value): any {
  return value ? AmcDisplayName.find(amc => amc.value === value)?.label : '-';
}